import React, { useRef, useState } from "react";
import { Avatar, Box, CircularProgress, Typography } from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";

const MAX_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

const AvatarUpload = ({ user, api, setUser }) => {
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const initials = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .map((n) => n.charAt(0).toUpperCase())
    .join("");

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!fileInputRef.current) return;
    fileInputRef.current.value = "";

    if (!file) return;
    setError(null);

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Only JPEG, PNG, or WebP images are supported.");
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setError("Image must be under 5MB.");
      return;
    }

    const formData = new FormData();
    formData.append("avatar", file);

    setUploading(true);
    try {
      const res = await api("/auth/avatar.php", {
        method: "POST",
        body: formData,
      });
      const newUrl = res.data.avatar_url;
      setAvatarUrl(newUrl);
      setUser((prev) => ({ ...prev, avatar_url: newUrl }));
    } catch (e) {
      setError(e.message || "Failed to upload image.");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setError(null);
    setRemoving(true);
    try {
      await api("/auth/avatar.php", { method: "DELETE" });
      setAvatarUrl(null);
      setUser((prev) => ({ ...prev, avatar_url: null }));
    } catch (e) {
      setError(e.message || "Failed to remove photo.");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 4 }}>
      {/* Clickable avatar */}
      <Box
        onClick={() => !uploading && !removing && fileInputRef.current?.click()}
        sx={{
          position: "relative",
          cursor: uploading || removing ? "default" : "pointer",
          borderRadius: "50%",
          flexShrink: 0,
          "&:hover .avatar-overlay": { opacity: 1 },
        }}
      >
        <Avatar
          src={avatarUrl || undefined}
          sx={{
            width: 72,
            height: 72,
            fontSize: 24,
            fontWeight: 600,
            backgroundColor: avatarUrl ? "transparent" : "grey.200",
            color: "text.primary",
            border: "1px solid",
            borderColor: "customDivider",
          }}
        >
          {!avatarUrl && initials}
        </Avatar>

        {/* Hover overlay */}
        {!uploading && !removing && (
          <Box
            className="avatar-overlay"
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              backgroundColor: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0,
              transition: "opacity 0.15s",
            }}
          >
            <CameraAltIcon sx={{ color: "white", fontSize: 22 }} />
          </Box>
        )}

        {/* Loading spinner overlay */}
        {(uploading || removing) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={22} sx={{ color: "text.secondary" }} />
          </Box>
        )}
      </Box>

      {/* Labels and remove link */}
      <Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontSize: 13, mb: 0.25 }}
          onClick={() => !uploading && !removing && fileInputRef.current?.click()}
          style={{ cursor: uploading || removing ? "default" : "pointer" }}
        >
          {avatarUrl ? "Change photo" : "Upload photo"}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
          JPEG, PNG or WebP — max 5MB
        </Typography>
        {avatarUrl && !uploading && !removing && (
          <Typography
            variant="caption"
            onClick={handleRemove}
            sx={{
              color: "#A32D2D",
              cursor: "pointer",
              display: "block",
              mt: 0.5,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Remove photo
          </Typography>
        )}
        {error && (
          <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </Box>
  );
};

export default AvatarUpload;
